import type * as React from "react"
import { createContext, useCallback, useContext, useMemo, useState } from "react"
import { ProfileSheet } from "@/features/preferences/ProfileSheet"

/**
 * ONE preferences sheet for the whole shell, openable from anywhere.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS. The signed-in person's own settings — language, text size,
 * theme (§11.4) — are a SHEET, not a screen, and deliberately so: he is mid-way
 * through a bill when he decides the type is too small, and a sheet leaves him
 * exactly where he was. See the header of `ProfileSheet.tsx`.
 *
 * That design had one consumer, the user menu, which owned the open state
 * itself. Then the search palette grew a "My settings" row pointing at
 * `routes.profile` — and `/profile` is not a route and never was. Selecting it
 * fell through to the catch-all and landed on the dashboard: the palette closed,
 * no sheet appeared, and nothing explained it. The entry with NO capability
 * gate, the one every signed-in person can see, was the only broken one.
 *
 * The fix is not to grow a `/profile` screen — that would contradict the sheet's
 * whole reason for being — but to let more than one trigger open the sheet that
 * already exists. So the open state lives ABOVE both of them, here, and there is
 * still exactly one `ProfileSheet` mounted in the shell. A second instance would
 * have been the easy way out and the wrong one: two sheets, two states, and a
 * later change made to whichever one the author happened to find.
 *
 * THE FALLBACK IS A NO-OP, matching `useGlobalSearchDialog`. A component
 * rendered outside this provider in a test should not blow up over a sheet it
 * never opens — but note that anything which actually OPENS preferences needs
 * the provider above it, which in the app is `AppShell`.
 * ─────────────────────────────────────────────────────────────────────────────
 */

interface PreferencesSheetContextValue {
  /** Show the signed-in person their own settings. */
  open: () => void
}

const PreferencesSheetContext = createContext<PreferencesSheetContextValue | null>(null)

export function usePreferencesSheet(): PreferencesSheetContextValue {
  const context = useContext(PreferencesSheetContext)
  return context ?? { open: () => undefined }
}

export function PreferencesSheetProvider({
  children,
}: {
  children: React.ReactNode
}): React.JSX.Element {
  const [open, setOpen] = useState(false)

  const show = useCallback(() => {
    setOpen(true)
  }, [])

  const value = useMemo<PreferencesSheetContextValue>(() => ({ open: show }), [show])

  return (
    <PreferencesSheetContext.Provider value={value}>
      {children}
      {/*
        * OUTSIDE whatever opened it, which is the point. A `SheetTrigger` inside
        * `DropdownMenuContent` is torn down in the same frame that opens the
        * sheet, because the menu unmounts its children when it closes — the
        * sheet flashes and vanishes. Hoisting the state to a component that
        * outlives every trigger is what keeps it on screen.
        */}
      <ProfileSheet open={open} onOpenChange={setOpen} />
    </PreferencesSheetContext.Provider>
  )
}
